import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { Card, PageTitle } from "./shared";

const LABELS = {
  landing_view: "Landing visits", intro_view: "Intro viewed", comparison_view: "Comparison viewed",
  questionnaire_start: "Questionnaire started", q_purpose: "Q1 Purpose", q_city: "Q2 City", q_area: "Q2b Area",
  q_type: "Q3 Property type", q_config: "Q4 Configuration", q_budget: "Q5 Budget", q_possession: "Q6 Possession",
  q_priorities: "Q7 Priorities", q_visit: "Q8 Visit timing", contact_view: "Contact form viewed", results_view: "Saw matched projects", matchmaking_requested: "Requested MatchMaking", contact_submitted: "Contact submitted", example_view: "Viewed MatchMaking Example", search_more: "Searched more after submitting", relook_matches: "Relooked at matches after submitting",
};

const Bars = ({ title, rows, testId }) => {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <Card data-testid={testId}>
      <p className="mb-4 font-semibold">{title}</p>
      {!rows.length && <p className="text-sm text-neutral-500">No data yet.</p>}
      <ul className="space-y-2.5">
        {rows.map((r) => (
          <li key={r.name} className="text-sm">
            <div className="flex justify-between"><span>{r.name}</span><span className="tabular-nums text-neutral-500">{r.count}</span></div>
            <div className="mt-1 h-1.5 rounded-full bg-neutral-100"><div className="h-full rounded-full bg-[#138808]" style={{ width: `${(r.count / max) * 100}%` }} /></div>
          </li>
        ))}
      </ul>
    </Card>
  );
};

export default function Funnel() {
  const { data } = useQuery({ queryKey: ["funnel"], queryFn: () => api.get("/staff/funnel").then((r) => r.data), staleTime: 10_000 });
  if (!data) return <p className="text-sm text-neutral-500">Loading…</p>;
  const top = Math.max(1, data.steps[0].count);
  const count = (e) => data.steps.find((s) => s.event === e)?.count || 0;
  const conv = count("contact_submitted");
  const views = count("example_view");

  return (
    <div data-testid="staff-funnel-page">
      <PageTitle>Funnel</PageTitle>
      <div className="mb-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Card><p className="text-xs text-neutral-500">Landing visits</p><p className="font-display text-3xl" data-testid="funnel-landing-count">{data.steps[0].count}</p></Card>
        <Card><p className="text-xs text-neutral-500">Total leads</p><p className="font-display text-3xl" data-testid="funnel-total-leads">{data.total_leads}</p></Card>
        <Card><p className="text-xs text-neutral-500">Visit → lead conversion</p><p className="font-display text-3xl text-[#138808]" data-testid="funnel-conversion">{((conv / top) * 100).toFixed(1)}%</p></Card>
        <Card data-testid="funnel-example-card">
          <p className="text-xs text-neutral-500">Viewed MatchMaking Example</p>
          <p className="font-display text-3xl text-[var(--saffron-deep)]" data-testid="funnel-example-views">{views}</p>
          <p className="text-xs text-neutral-500" data-testid="funnel-example-rate">{conv ? ((views / conv) * 100).toFixed(0) : 0}% of submitted leads</p>
        </Card>
      </div>
      <Card className="mb-5" data-testid="funnel-steps">
        <p className="mb-4 font-semibold">Step-by-step drop-off</p>
        <ul className="space-y-2">
          {data.steps.map((s, i) => {
            const prev = i ? data.steps[i - 1].count : s.count;
            const drop = prev ? Math.max(0, 100 - (s.count / prev) * 100) : 0;
            return (
              <li key={s.event} className="grid grid-cols-[minmax(130px,190px)_1fr_auto] items-center gap-3 text-sm" data-testid={`funnel-step-${s.event}`}>
                <span className="truncate">{LABELS[s.event]}</span>
                <div className="h-6 rounded-md bg-neutral-100">
                  <div className="h-full rounded-md bg-gradient-to-r from-[#ff9933] to-[#ffb766]" style={{ width: `${(s.count / top) * 100}%` }} />
                </div>
                <span className="w-28 text-right tabular-nums text-neutral-600">
                  {s.count} {i > 0 && <span className={drop > 30 ? "text-red-600" : "text-neutral-400"}>−{drop.toFixed(0)}%</span>}
                </span>
              </li>
            );
          })}
        </ul>
        <p className="mt-4 text-xs text-neutral-400">Anonymous, one count per visitor session per step. No personal details are stored in events.</p>
      </Card>
      <div className="grid gap-5 md:grid-cols-2">
        <Bars title="Leads by country" rows={data.by_country} testId="funnel-by-country" />
        <Bars title="Leads by city" rows={data.by_city} testId="funnel-by-city" />
      </div>
    </div>
  );
}
